/**
 * (Re)generate-FAMILY-report confirm — the combined-page sibling of
 * RegenerateDialog (COMBINED-V2-DATA-MAP §3, "family regenerate"). Entry
 * points (header kebab + failed-card CTA) only render with the regenerate
 * permission, a non-decided household, and ≥2 eligible co-residents
 * (deriveFamilyReportAction); the `/resources/family-summary` endpoint
 * re-checks permission, readiness, and the in-progress guard.
 *
 * Two `mode`s:
 * - `'regenerate'` — a completed family report exists; re-running REPLACES it
 *   (danger copy) and posts `regenerate=true`.
 * - `'generate'` — failed/missing report, nothing to replace (plain copy).
 *
 * Include/exclude: the body is a checkbox list of CO-RESIDENTS only
 * (guarantors are never selectable — completed guarantors are auto-included
 * as output_processing_id strings in the Family Summary request payload).
 * A member is selectable only once their own report is finished
 * (`reportCompleted`); the rest render disabled. The selection posts as
 * `applicantIds` (JSON), so a landlord can drop a member from the run.
 *
 * No useEffect: result handling uses the ref-callback marker idiom; the
 * selection resets on open via the render-time open-transition pattern.
 */
import { ArrowRight } from 'lucide-react'
import { useRef, useState } from 'react'
import { useFetcher, useRevalidator } from 'react-router'
import { toast } from 'sonner'
import type { ReportActionMode } from '#app/utils/application-detail-v2/derive'
import type { GroupMember } from '#app/utils/application-detail-v2/types'
import { cn } from '#app/utils/misc'
import { DetailDialog, PillButton } from '../dialogs/dialog-bits'

type FamilySummaryResult =
	| { success: true; familyScoreId?: string; groupId?: string }
	| { success: false; error?: string }

export function FamilyRegenerateDialog({
	open,
	onOpenChange,
	groupId,
	members,
	mode = 'regenerate',
	isStudentHousing = false,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	/** The household's group id — present whenever this dialog can open (a solo
	 * application has < 2 co-residents, so the action is hidden). */
	groupId: string | null
	members: GroupMember[]
	/** 'generate' for a failed/missing report, 'regenerate' for a finished one. */
	mode?: ReportActionMode
	/** Student housing: allow selecting a single applicant when a completed
	 * guarantor exists in the group. */
	isStudentHousing?: boolean
}) {
	const isGenerate = mode === 'generate'
	const fetcher = useFetcher<FamilySummaryResult>()
	const revalidator = useRevalidator()
	const busy = fetcher.state !== 'idle'

	// Guarantors are never checkbox-selected — only co-residents are listed.
	// Completed guarantors are still sent as output_processing_ids in the
	// Family Summary payload (buildFamilySummaryPayload).
	const coResidents = members.filter((m) => m.applicationType !== 'guarantor')
	const completedGuarantorCount = members.filter(
		(m) => m.applicationType === 'guarantor' && m.reportCompleted,
	).length
	const defaultSelected = () =>
		new Set(
			coResidents.filter((m) => m.reportCompleted).map((m) => m.applicationId),
		)

	const [selected, setSelected] = useState<Set<string>>(defaultSelected)
	const [wasOpen, setWasOpen] = useState(open)
	if (open !== wasOpen) {
		setWasOpen(open)
		// Re-seed the selection each time the dialog opens (no useEffect).
		if (open) setSelected(defaultSelected())
	}

	const toggle = (id: string) =>
		setSelected((prev) => {
			const next = new Set(prev)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})

	const minApplicants = isStudentHousing ? 1 : 2
	const tooFew = selected.size < minApplicants
	const missingStudentGuarantor =
		isStudentHousing && completedGuarantorCount < 1
	const cannotSubmit = tooFew || missingStudentGuarantor

	const lastHandled = useRef<FamilySummaryResult | null>(null)
	const result = fetcher.state === 'idle' ? fetcher.data : undefined
	const marker =
		result && lastHandled.current !== result ? (
			<span
				hidden
				ref={() => {
					if (lastHandled.current === result) return
					lastHandled.current = result
					if (result.success) {
						toast.info(
							`Family report ${
								isGenerate ? 'generation' : 'regeneration'
							} started. Processing…`,
						)
						onOpenChange(false)
						void revalidator.revalidate()
					} else {
						toast.error(result.error || 'Failed to start the family report')
					}
				}}
			/>
		) : null

	return (
		<>
			{marker}
			<DetailDialog
				open={open}
				onOpenChange={onOpenChange}
				title={
					isGenerate ? 'Generate Family Report' : 'Regenerate Family Report'
				}
				description={
					isGenerate
						? 'Select which applicants to include in the family report. ' +
							'Only members whose own report has finished can be included.'
						: 'This replaces the current family report with a freshly generated ' +
							'one and moves it back to Processing. Select which applicants to ' +
							'include — the existing report cannot be recovered afterwards.'
				}
				className="max-h-[90vh] overflow-y-auto [scrollbar-width:thin] sm:max-w-xl"
			>
				<ul className="flex flex-col gap-2">
					{coResidents.map((member) => {
						const checked = selected.has(member.applicationId)
						const disabled = !member.reportCompleted
						return (
							<li key={member.applicationId}>
								<label
									className={cn(
										'flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm',
										disabled
											? 'cursor-not-allowed border-[#F0F0F0] opacity-60'
											: 'cursor-pointer border-[#ECECEA] hover:bg-[#F6F7F5]',
									)}
								>
									<span className="flex items-center gap-3">
										<input
											type="checkbox"
											className="size-4 accent-[#32302F]"
											checked={checked}
											disabled={disabled}
											onChange={() => toggle(member.applicationId)}
										/>
										<span className="font-medium text-[#32302F]">
											{member.displayName}
										</span>
									</span>
									<span className="text-[13px] text-[#747474]">
										{disabled
											? 'Report not ready'
											: member.applicationType === 'primary'
												? 'Primary'
												: 'Co-Applicant'}
									</span>
								</label>
							</li>
						)
					})}
				</ul>

				{tooFew ? (
					<p className="text-[13px] text-[#DC2634]">
						{isStudentHousing
							? 'Select at least 1 applicant to generate a family report.'
							: 'Select at least 2 applicants to generate a family report.'}
					</p>
				) : null}
				{missingStudentGuarantor ? (
					<p className="text-[13px] text-[#DC2634]">
						A completed guarantor is required for a student housing family
						report.
					</p>
				) : null}

				<div className="flex justify-end gap-3">
					<PillButton
						tone="outline"
						onClick={() => onOpenChange(false)}
						disabled={busy}
					>
						Cancel
					</PillButton>
					<PillButton
						tone={isGenerate ? 'dark' : 'danger'}
						disabled={busy || cannotSubmit || !groupId}
						onClick={() => {
							if (!groupId || cannotSubmit) return
							void fetcher.submit(
								{
									groupId,
									regenerate: isGenerate ? 'false' : 'true',
									applicantIds: JSON.stringify([...selected]),
								},
								{ method: 'post', action: '/resources/family-summary' },
							)
						}}
					>
						{busy
							? 'Starting…'
							: isGenerate
								? 'Generate Report'
								: 'Regenerate Report'}
						<ArrowRight className="size-4" />
					</PillButton>
				</div>
			</DetailDialog>
		</>
	)
}
